// englishcontent5.js — Section 5: Improving Your Writing
// Grade 9 Ready — enhanced full section
// Improvements added:
// - examinerTip
// - commonMistakes
// - grade9Move
// - keyVocabulary
// - examFocus
// - quickRecall where useful
//
// Alignment focus:
// - accurate, fluent and effective writing
// - Standard English, grammar, punctuation and spelling
// - vocabulary, sentence structure and organisation for effect
// - planning, drafting, editing and proofreading
// - short, precise quotation and analytical explanation
// - comparison, evaluation and essay structure

const makeTopic = (topic) => ({
  duration: '25-35 mins',
  xp: 100,
  difficulty: 'foundation',
  examFocus: {
    ao5:
      'Communicate clearly, effectively and imaginatively; organise information and ideas for coherence and impact.',
    ao6:
      'Use vocabulary, sentence structures, spelling and punctuation accurately and deliberately for clarity, purpose and effect.',
  },
  ...topic,
});

const writingSkill = ({
  id,
  slug,
  title,
  summary,
  explainer,
  interactiveLesson,
  workedExample,
  practice,
  applicationTask,
  aiFeedback,
  mastery,
  keyVocabulary = [],
  examinerTip = '',
  commonMistakes = [],
  grade9Move = '',
  quickRecall = [],
  examFocus,
}) =>
  makeTopic({
    id,
    slug,
    title,
    summary,
    explainer,
    interactiveLesson,
    workedExample,
    practice,
    applicationTask,
    aiFeedback,
    mastery,
    keyVocabulary,
    examinerTip,
    commonMistakes,
    grade9Move,
    quickRecall,
    ...(examFocus ? { examFocus } : {}),
  });

export const englishContent5 = {
  id: 5,
  title: 'Improving Your Writing',
  slug: 'improving-your-writing',
  color: 'sky',
  icon: 'PenTool',
  description:
    'Punctuation, grammar, paragraphs, vocabulary, essay-writing, quoting, comparing and proofreading.',
  topics: [

    // =========================================================
    // TOPIC 1 — BASIC PUNCTUATION
    // =========================================================
    writingSkill({
      id: 'eng-5-1',
      slug: 'basic-punctuation',
      title: 'Basic Punctuation',
      summary: 'Full stops, capitals, question marks and exclamation marks.',
      keyVocabulary: ['capital letter', 'full stop', 'question mark', 'exclamation mark', 'proper noun', 'sentence boundary'],
      examinerTip:
        'Basic punctuation is not basic in marks terms. If sentence boundaries are insecure, even good ideas become hard to reward.',
      commonMistakes: [
        'Forgetting capital letters for names, places, days and months.',
        'Using a question mark after an indirect question.',
        'Using too many exclamation marks for emphasis.'
      ],
      grade9Move:
        'Control sentence endings deliberately: use full stops for clarity, questions for reader engagement and exclamation marks only when the tone genuinely demands intensity.',
      explainer: {
        title: 'Basic Punctuation',
        content: `
Punctuation marks tell the reader where to pause, stop and change tone.

Core rules:
- Every sentence begins with a capital letter.
- Every complete sentence ends with a full stop ( . ), question mark ( ? ) or exclamation mark ( ! ).
- Use a capital letter for names of people, places, days, months, titles and the pronoun "I".

Choose your end mark carefully:
- Full stop: a plain statement. "The bell rang."
- Question mark: a direct question. "Are you ready?"
- Exclamation mark: strong feeling. Use sparingly — one "!" is usually enough.

A direct question uses a question mark:
"Where is Mum?"

An indirect question usually uses a full stop:
"My brother asked where Mum was."

Correct basic punctuation is the foundation of clear writing. Without it, meaning falls apart.
`
      },
      interactiveLesson: {
        title: 'End Marks',
        steps: [
          { type: 'question', question: 'Which ends a direct question?', options: ['?', '!'], correct: 0 },
          { type: 'question', question: 'Every sentence begins with:', options: ['A capital letter', 'A comma'], correct: 0 },
          { type: 'question', question: 'Days of the week should:', options: ['Start with a capital', 'Be all lowercase'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "the train to london leaves on monday"

Model answer:
Corrected: "The train to London leaves on Monday."

Capitals are needed for the start of the sentence, for "London" because it is a place, and for "Monday" because it is a day. The full stop closes the statement.
`
      },
      practice: [
        { type: 'mcq', question: 'Which is correct?', options: ['"Is it raining?"', '"Is it raining."'], correct: 0 },
        { type: 'mcq', question: 'Which needs capital letters?', options: ['"jake went to manchester on friday."', '"The door was open."'], correct: 0 },
        { type: 'true-false', question: 'Exclamation marks should be used on every sentence.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite this with correct punctuation: "my brother asked where is mum"`,
        sentenceStarter: 'My brother asked...',
        hints: ['Use a capital letter for "My".', 'Think carefully: is this a direct question or reported/indirect speech?']
      },
      aiFeedback: {
        successCriteria: ['Capital letters correct', 'Correct sentence end mark', 'Meaning preserved']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does overusing exclamation marks make writing feel weaker?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 2 — SENTENCES, PHRASES AND CLAUSES
    // =========================================================
    writingSkill({
      id: 'eng-5-2',
      slug: 'sentences-phrases-and-clauses',
      title: 'Sentences, Phrases and Clauses',
      summary: 'Build clear ideas with the right structural units.',
      keyVocabulary: ['phrase', 'clause', 'main clause', 'subordinate clause', 'simple sentence', 'compound sentence', 'complex sentence'],
      examinerTip:
        'Sentence control is one of the clearest signs of confident writing. A student who can vary sentences accurately usually sounds more mature.',
      commonMistakes: [
        'Calling every short group of words a clause, even when it has no verb.',
        'Using subordinate clauses as sentence fragments by accident.',
        'Writing every sentence in the same pattern.'
      ],
      grade9Move:
        'Use sentence type to control meaning: simple sentences for impact, compound sentences for balance, and complex sentences to show cause, contrast, time or condition.',
      explainer: {
        title: 'Sentences, Phrases and Clauses',
        content: `
A phrase is a short group of words without a finite verb:
- "in the garden"
- "under the old bridge"
- "a cold, silent morning"

A clause is a group of words with a subject and a verb:
- Main clause: makes sense on its own. "She smiled."
- Subordinate clause: does not make complete sense on its own. "when the door opened"

A sentence is one or more clauses.

Three main sentence types:
- Simple: one main clause. "The rain stopped."
- Compound: two main clauses joined by a coordinating conjunction such as and, but, so or or. "The rain stopped, and the sun came out."
- Complex: a main clause plus a subordinate clause. "When the rain stopped, the sun came out."

Varying these types makes writing more interesting and controls pace.
`
      },
      interactiveLesson: {
        title: 'Sentence Types',
        steps: [
          { type: 'question', question: 'A subordinate clause:', options: ['Does not make complete sense alone', 'Always makes complete sense alone'], correct: 0 },
          { type: 'question', question: '"The dog barked." is a:', options: ['Simple sentence', 'Complex sentence'], correct: 0 },
          { type: 'question', question: '"When the bell rang, we left." is a:', options: ['Complex sentence', 'Simple sentence'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "I was cold. I put on a coat."

Model answer:
These are two simple sentences. Joined as a complex sentence:
"Because I was cold, I put on a coat."

This makes the cause-and-effect link clearer and reads more fluently.
`
      },
      practice: [
        { type: 'mcq', question: '"In the garden" is a:', options: ['Phrase', 'Clause'], correct: 0 },
        { type: 'mcq', question: '"Although it was late" is a:', options: ['Subordinate clause', 'Complete simple sentence'], correct: 0 },
        { type: 'true-false', question: 'A main clause can stand alone as a sentence.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write one simple, one compound and one complex sentence about a school morning.`,
        sentenceStarter: 'Simple: ...\nCompound: ...\nComplex: ...',
        hints: ['Use "and", "but" or "so" in the compound sentence.', 'Use "because", "when", "although" or "if" in the complex sentence.']
      },
      aiFeedback: {
        successCriteria: ['Uses all three sentence types', 'Each sentence is grammatically correct', 'Sentence type supports meaning']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does mixing sentence types feel more natural than using only one type?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 3 — COMMAS
    // =========================================================
    writingSkill({
      id: 'eng-5-3',
      slug: 'commas',
      title: 'Commas',
      summary: 'Lists, pauses, clauses and extra information.',
      keyVocabulary: ['comma', 'introductory clause', 'embedded clause', 'non-essential information', 'comma splice', 'list'],
      examinerTip:
        'Commas should clarify structure, not decorate sentences. A well-placed comma can make a complex sentence much easier to read.',
      commonMistakes: [
        'Joining two full sentences with only a comma.',
        'Putting commas wherever you would pause in speech.',
        'Forgetting the comma after a fronted subordinate clause.'
      ],
      grade9Move:
        'Use commas to shape meaning: separate clauses, mark embedded information, and avoid ambiguity without overloading the sentence.',
      explainer: {
        title: 'Commas',
        content: `
Commas mark small pauses and separate parts of a sentence. Four main uses:

1. Lists:
"We bought apples, oranges and pears."

2. After an introductory clause or phrase:
"When the rain stopped, we went outside."
"Suddenly, the lights went out."

3. Adding extra information:
"My uncle, who lives in Leeds, called me."

4. Separating clauses where the structure needs clarity:
"Although it was late, he kept reading."

Avoid the comma splice. A comma splice joins two complete sentences with only a comma:
Wrong: "She was late, she ran."
Better: "She was late, so she ran."
Better: "She was late. She ran."
Better: "She was late; she ran."

Commas should not appear everywhere. Too many commas make writing choppy and uncertain.
`
      },
      interactiveLesson: {
        title: 'Comma Uses',
        steps: [
          { type: 'question', question: 'Which is correct?', options: ['"I bought tea, bread and milk."', '"I bought tea bread, and milk."'], correct: 0 },
          { type: 'question', question: 'A comma splice joins:', options: ['Two sentences with just a comma', 'Items in a list'], correct: 0 },
          { type: 'question', question: '"Suddenly, a noise..." — the comma comes after:', options: ['An introductory word', 'A name'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "She finished her homework, she went to bed."

Model answer:
This is a comma splice.

Correct versions:
- "She finished her homework, and she went to bed."
- "She finished her homework. She went to bed."
- "She finished her homework; she went to bed."

Each version fixes the sentence boundary.
`
      },
      practice: [
        { type: 'mcq', question: 'Which needs a comma?', options: ['"Although it was late he kept reading."', '"He kept reading."'], correct: 0 },
        { type: 'mcq', question: 'Which is a comma splice?', options: ['"The door opened, the room fell silent."', '"When the door opened, the room fell silent."'], correct: 0 },
        { type: 'true-false', question: 'A comma can join two full sentences on its own.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Add commas where needed: "after the bell rang the teacher who had been marking books stood up and said time's up"`,
        sentenceStarter: 'After the bell rang,...',
        hints: ['Look for the introductory clause.', 'Look for extra information about the teacher.']
      },
      aiFeedback: {
        successCriteria: ['Introductory comma used', 'Extra information marked clearly', 'No comma splice']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `How do too many commas damage a sentence as much as too few?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 4 — COLONS
    // =========================================================
    writingSkill({
      id: 'eng-5-4',
      slug: 'colons',
      title: 'Colons',
      summary: 'Introduce lists, quotes and explanations.',
      keyVocabulary: ['colon', 'complete clause', 'list', 'explanation', 'quotation'],
      examinerTip:
        'A colon creates expectation. It tells the reader that the second part will explain, reveal or expand the first part.',
      commonMistakes: [
        'Using a colon after a verb such as "include".',
        'Putting a colon after an incomplete sentence stem.',
        'Using a colon where a comma would be more natural.'
      ],
      grade9Move:
        'Use a colon to create a controlled reveal: make the first clause build expectation, then let the second clause deliver the explanation or impact.',
      explainer: {
        title: 'Colons',
        content: `
A colon ( : ) introduces something that follows.

Main uses:
- Introduce a list: "I packed three things: my phone, my keys and a book."
- Introduce a quotation: He whispered: "We have to leave."
- Introduce an explanation: "The result is clear: we need more time."
- Create emphasis or reveal: "Only one thing mattered: survival."

Rules:
- The words before the colon should usually form a complete clause.
- Do not use a colon straight after verbs like "include" or "are".
  Awkward: "The ingredients are: flour, eggs and milk."
  Better: "The recipe needs three ingredients: flour, eggs and milk."

Colons can make writing sound precise, confident and controlled.
`
      },
      interactiveLesson: {
        title: 'Colons',
        steps: [
          { type: 'question', question: 'A colon can introduce:', options: ['A list or explanation', 'The middle of a word'], correct: 0 },
          { type: 'question', question: 'What should usually come before the colon?', options: ['A complete clause', 'A single word only'], correct: 0 },
          { type: 'question', question: '"I like: apples and pears" is:', options: ['Incorrect', 'Correct'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "She made a decision she would leave at dawn."

Model answer:
"She made a decision: she would leave at dawn."

The colon introduces the explanation. It signals, "Here is what the decision was."
`
      },
      practice: [
        { type: 'mcq', question: 'Which is correct?', options: ['"I packed: clothes, a book."', '"I packed three things: clothes, a book and a pen."'], correct: 1 },
        { type: 'mcq', question: 'A colon often means:', options: ['Here comes the explanation', 'This sentence is finished completely'], correct: 0 },
        { type: 'true-false', question: 'A colon can be used before a list.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write one sentence using a colon to introduce an explanation.`,
        sentenceStarter: 'The reason was simple:',
        hints: ['Make the part before the colon a full clause.', 'The second part should explain the first part.']
      },
      aiFeedback: {
        successCriteria: ['Uses colon correctly', 'First part is a full clause', 'Second part explains or expands the first']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does a colon often feel more formal or controlled than a dash in the same place?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 5 — SEMICOLONS
    // =========================================================
    writingSkill({
      id: 'eng-5-5',
      slug: 'semicolons',
      title: 'Semicolons',
      summary: 'Join two linked sentences without a conjunction.',
      keyVocabulary: ['semicolon', 'main clause', 'compound sentence', 'complex list', 'sentence boundary'],
      examinerTip:
        'Semicolons are impressive only when accurate. They must join closely related main clauses or separate complex list items.',
      commonMistakes: [
        'Using a semicolon before a subordinate clause.',
        'Using a semicolon as a stronger comma.',
        'Using semicolons too often, which can make writing feel forced.'
      ],
      grade9Move:
        'Use one well-placed semicolon to show a close conceptual relationship between two balanced ideas.',
      explainer: {
        title: 'Semicolons',
        content: `
A semicolon ( ; ) is stronger than a comma but softer than a full stop.

Main uses:
1. Join two closely related main clauses:
"The night was quiet; the city slept."

2. Separate items in a complex list where commas alone would be confusing:
"I visited Paris, France; Rome, Italy; and Lisbon, Portugal."

Rules:
- When joining clauses, both sides must make sense on their own.
- Do not use a semicolon after a subordinating conjunction such as "although", "because", "when" or "if".
- Use semicolons sparingly.

A semicolon should feel purposeful, not decorative.
`
      },
      interactiveLesson: {
        title: 'Semicolons',
        steps: [
          { type: 'question', question: 'A semicolon can join:', options: ['Two linked main clauses', 'A clause and a phrase'], correct: 0 },
          { type: 'question', question: 'Each side of a semicolon must:', options: ['Be a full main clause', 'Be a single word'], correct: 0 },
          { type: 'question', question: 'Semicolons are strongest in:', options: ['Linking related ideas', 'Showing questions'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "The lecture ended, the students applauded."

Model answer:
"The lecture ended; the students applauded."

The semicolon shows the two sentences are closely linked — one follows naturally from the other — without needing a conjunction.
`
      },
      practice: [
        { type: 'mcq', question: 'Which is correct?', options: ['"She smiled; he smiled back."', '"She smiled; because he smiled back."'], correct: 0 },
        { type: 'mcq', question: 'Which side of a semicolon must be complete?', options: ['Both sides', 'Only the first side'], correct: 0 },
        { type: 'true-false', question: 'A semicolon joins clauses more lightly than a full stop.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite these two sentences using a semicolon: "The storm broke. The trees shook."`,
        sentenceStarter: 'The storm broke;...',
        hints: ['Do not add a conjunction.', 'Check both halves make sense alone.']
      },
      aiFeedback: {
        successCriteria: ['Uses a semicolon correctly', 'Both halves are full clauses', 'The two ideas are closely linked']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `When is a full stop actually better than a semicolon?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 6 — APOSTROPHES
    // =========================================================
    writingSkill({
      id: 'eng-5-6',
      slug: 'apostrophes',
      title: 'Apostrophes',
      summary: 'Contractions and possession.',
      keyVocabulary: ['apostrophe', 'contraction', 'possession', 'singular possession', 'plural possession', 'its', "it's"],
      examinerTip:
        'Apostrophe errors are easy to spot. Accurate apostrophes immediately make writing look more controlled.',
      commonMistakes: [
        'Confusing "its" and "it’s".',
        'Writing plural nouns with apostrophes when there is no possession.',
        'Putting the apostrophe before the s for plural possession.'
      ],
      grade9Move:
        'Proofread apostrophes separately from other punctuation: check contraction first, then ownership, then plural ownership.',
      explainer: {
        title: 'Apostrophes',
        content: `
An apostrophe ( ' ) has two main jobs:

1. Contraction — showing missing letters:
- "do not" becomes "don't"
- "it is" becomes "it's"
- "I am" becomes "I'm"

2. Possession — showing ownership:
- Singular: add 's. "The dog's bowl."
- Plural ending in -s: add just '. "The dogs' bowls."
- Plural not ending in -s: add 's. "The children's books."

Watch out:
- "its" means belonging to it. "The cat licked its paw."
- "it's" means "it is" or "it has". "It's raining."

Apostrophes are small but powerful. Using them wrongly can change meaning.
`
      },
      interactiveLesson: {
        title: 'Apostrophes',
        steps: [
          { type: 'question', question: '"do not" written with a contraction is:', options: ["don't", 'dont'], correct: 0 },
          { type: 'question', question: 'The bowl belonging to one dog:', options: ["the dog's bowl", 'the dogs bowl'], correct: 0 },
          { type: 'question', question: '"It\'s raining" means:', options: ['It is raining', 'Belonging to it'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "Its the childrens classroom."

Model answer:
"It's the children's classroom."

The first apostrophe shows "It is". The second apostrophe shows possession because the classroom belongs to the children.
`
      },
      practice: [
        { type: 'mcq', question: 'The bowls belonging to three dogs:', options: ["the dogs' bowls", "the dog's bowls"], correct: 0 },
        { type: 'mcq', question: '"The cats played outside" needs an apostrophe if:', options: ['Something belongs to the cats', 'There is more than one cat'], correct: 0 },
        { type: 'true-false', question: '"It\'s" with an apostrophe means "it is" or "it has".', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite with correct apostrophes: "the teachers classroom is tidy but the students work isnt finished"`,
        sentenceStarter: "The teacher's...",
        hints: ['Possession on "teacher".', 'Possession on "student" may be singular or plural depending on your meaning.', 'Contraction on "is not".']
      },
      aiFeedback: {
        successCriteria: ['Apostrophes for possession correct', 'Contractions correct', 'Meaning is clear']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is confusing "its" and "it's" so common, and how can you remember the difference?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 7 — INVERTED COMMAS
    // =========================================================
    writingSkill({
      id: 'eng-5-7',
      slug: 'inverted-commas',
      title: 'Inverted Commas',
      summary: 'Quoting speech, titles and words.',
      keyVocabulary: ['inverted commas', 'quotation marks', 'direct speech', 'speech tag', 'embedded quotation', 'dialogue'],
      examinerTip:
        'For analysis, short embedded quotations are usually stronger than long copied lines. For creative writing, dialogue must be punctuated cleanly.',
      commonMistakes: [
        'Forgetting the comma before direct speech.',
        'Putting punctuation outside the closing inverted comma when it belongs to the speech.',
        'Not starting a new line for a new speaker.'
      ],
      grade9Move:
        'Use dialogue to reveal character indirectly: let word choice, pauses and speech tags show mood rather than explaining it.',
      explainer: {
        title: 'Inverted Commas',
        content: `
Inverted commas are also called quotation marks or speech marks.

They are used for:

1. Direct speech — the exact words someone says:
"I'll see you tomorrow," she said.

2. Quotations from a text:
The poet writes "the night was silver".

3. Titles of short works such as poems, articles and songs:
"Ode on a Grecian Urn".

Rules:
- Punctuation linked to the quote or speech usually goes inside the closing mark.
- Start a new line for each new speaker in dialogue.
- Use single ('...') or double ("...") inverted commas consistently. Many schools use double marks for direct speech; some use single marks. The key is accuracy and consistency.

Accurate punctuation around quotes shows you are a careful reader and writer.
`
      },
      interactiveLesson: {
        title: 'Quoting',
        steps: [
          { type: 'question', question: 'Inverted commas can show:', options: ['Direct speech or quoted words', 'A list'], correct: 0 },
          { type: 'question', question: 'Should each new speaker start on a new line?', options: ['Yes', 'No'], correct: 0 },
          { type: 'question', question: 'A poem title can be marked with:', options: ['Inverted commas', 'A semicolon'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: She said I will come later

Model answer:
She said, "I will come later."

The comma introduces the speech, and the full stop goes inside the closing inverted comma because it belongs to the spoken sentence.
`
      },
      practice: [
        { type: 'mcq', question: 'The full stop in direct speech usually goes:', options: ['Inside the closing inverted comma', 'Outside every time'], correct: 0 },
        { type: 'mcq', question: 'A new speaker usually means:', options: ['A new line', 'A semicolon'], correct: 0 },
        { type: 'true-false', question: 'You should start a new line for each new speaker.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write two lines of dialogue between two characters, using inverted commas correctly.`,
        sentenceStarter: '"Are you ready?" she asked.\n"Almost," he replied.',
        hints: ['New line for each speaker.', 'Punctuation inside the marks where it belongs.', 'Use speech tags carefully.']
      },
      aiFeedback: {
        successCriteria: ['Correct inverted commas', 'Punctuation placed correctly', 'New line per speaker']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is dialogue often more engaging than description alone?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 8 — BRACKETS, DASHES AND HYPHENS
    // =========================================================
    writingSkill({
      id: 'eng-5-8',
      slug: 'brackets-dashes-and-hyphens',
      title: 'Brackets, Dashes and Hyphens',
      summary: 'Add extra info, dramatic emphasis and joined-up words.',
      keyVocabulary: ['brackets', 'parenthesis', 'dash', 'hyphen', 'non-essential information', 'emphasis'],
      examinerTip:
        'Brackets, dashes and hyphens do different jobs. Confusing a hyphen with a dash is a common technical error.',
      commonMistakes: [
        'Using a hyphen instead of a dash for parenthesis.',
        'Putting essential information inside brackets.',
        'Using too many dashes so the writing feels breathless.'
      ],
      grade9Move:
        'Choose the punctuation according to tone: brackets for a quiet aside, dashes for interruption or emphasis, hyphens for compound description.',
      explainer: {
        title: 'Brackets, Dashes and Hyphens',
        content: `
Brackets ( ) enclose extra information that is not essential. Removing the bracketed part should still leave a complete sentence.
- "Our school (which opened in 1964) is celebrating an anniversary."

Dashes — either single or in pairs — add stronger, more dramatic extra information.
- "There was only one answer — silence."
- "She could — and would — speak out."

Hyphens - join words into one idea.
- "well-known author"
- "state-of-the-art design"
- "two-year course"

Do not confuse:
- Hyphen (-): short mark that joins words.
- Dash (—): longer mark that adds extra information, interruption or emphasis.
`
      },
      interactiveLesson: {
        title: 'Brackets, Dashes, Hyphens',
        steps: [
          { type: 'question', question: 'Which mark adds the most dramatic emphasis?', options: ['Dash', 'Brackets'], correct: 0 },
          { type: 'question', question: '"Well-known" uses a:', options: ['Hyphen', 'Dash'], correct: 0 },
          { type: 'question', question: 'Brackets usually add:', options: ['Extra, non-essential information', 'The main idea'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "The winner, finally, was announced."

Model answer:
More controlled versions:
- "The winner — finally — was announced."
- "The winner (finally) was announced."

The dashes add urgency; the brackets add a quieter aside.
`
      },
      practice: [
        { type: 'mcq', question: '"State-of-the-art" uses:', options: ['Hyphens', 'Dashes'], correct: 0 },
        { type: 'mcq', question: 'Which is usually best for a quiet aside?', options: ['Brackets', 'A dash'], correct: 0 },
        { type: 'true-false', question: 'Brackets should contain non-essential information.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write one sentence using a pair of dashes for dramatic effect.`,
        sentenceStarter: '',
        hints: ['Put non-essential but emphatic information between the dashes.', 'Check the sentence still works if the dashed section is removed.']
      },
      aiFeedback: {
        successCriteria: ['Uses a dash pair correctly', 'Still reads clearly if dashes are removed', 'Effect is deliberate']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why might a novelist prefer dashes to brackets for the same extra information?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 9 — TENSES
    // =========================================================
    writingSkill({
      id: 'eng-5-9',
      slug: 'tenses',
      title: 'Tenses',
      summary: 'Past, present, future and consistency.',
      keyVocabulary: ['tense', 'past tense', 'present tense', 'future tense', 'continuous', 'perfect', 'flashback'],
      examinerTip:
        'Tense shifts are one of the quickest ways to make writing feel uncontrolled. Only shift tense when there is a clear reason.',
      commonMistakes: [
        'Mixing past and present in the same event.',
        'Changing tense during a description without purpose.',
        'Using dialogue tense correctly but narrative tense incorrectly around it.'
      ],
      grade9Move:
        'Use tense shifts deliberately: present tense can create immediacy, past tense can control narrative distance, and flashbacks can use past perfect forms such as "had seen".',
      explainer: {
        title: 'Tenses',
        content: `
Tense tells the reader when an action happens.

Three main tenses:
- Past: "She walked home."
- Present: "She walks home."
- Future: "She will walk home."

Each has further forms:
- Continuous: "She was walking."
- Perfect: "She had walked."
- Future perfect: "She will have walked."

The key skill is consistency:
- Pick a main tense for your piece.
- Only switch if there is a clear reason, such as a flashback, a general truth or direct speech.

Common mistake:
"I was walking home when I see a dog."

Correction:
"I was walking home when I saw a dog."

Consistent tense keeps writing clear and easy to follow.
`
      },
      interactiveLesson: {
        title: 'Tenses',
        steps: [
          { type: 'question', question: '"She had finished" is:', options: ['Past perfect', 'Future simple'], correct: 0 },
          { type: 'question', question: 'Which is consistent?', options: ['"I ran and I jumped."', '"I run and I jumped."'], correct: 0 },
          { type: 'question', question: '"She will have arrived" is:', options: ['Future perfect', 'Past simple'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "The wind was howling. He opens the window."

Model answer:
The tense shifts from past to present, which feels jarring.

Corrected:
"The wind was howling. He opened the window."

Keeping both verbs in the past tense makes the moment flow.
`
      },
      practice: [
        { type: 'mcq', question: '"She runs to the bus" is:', options: ['Present', 'Past'], correct: 0 },
        { type: 'mcq', question: '"He had forgotten" suggests:', options: ['An action before another past action', 'A future action'], correct: 0 },
        { type: 'true-false', question: 'Switching tense randomly can confuse the reader.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite keeping tense consistent: "I opened the door, and then I see my friend, who waves at me."`,
        sentenceStarter: 'I opened the door, and then I saw...',
        hints: ['Keep everything in the past.', 'Check every verb.']
      },
      aiFeedback: {
        successCriteria: ['All narrative verbs in the same tense', 'Meaning preserved', 'Sentence reads fluently']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `When is switching tense actually useful, such as in flashbacks or direct speech?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 10 — STANDARD ENGLISH
    // =========================================================
    writingSkill({
      id: 'eng-5-10',
      slug: 'standard-english',
      title: 'Standard English',
      summary: 'Formal grammar, subject–verb agreement and avoiding dialect in formal writing.',
      keyVocabulary: ['Standard English', 'dialect', 'register', 'formal', 'informal', 'subject-verb agreement', 'double negative'],
      examinerTip:
        'Standard English does not mean better speech. It means the accepted formal written form expected in essays, exams and official contexts.',
      commonMistakes: [
        'Using "was" with plural subjects.',
        'Using double negatives in formal writing.',
        'Using "done" instead of "did" as the past-tense verb.'
      ],
      grade9Move:
        'Control register deliberately: use Standard English in formal writing, but use dialect or non-standard forms thoughtfully in dialogue to reveal character, setting and voice.',
      explainer: {
        title: 'Standard English',
        content: `
Standard English is the formal version of English used in schools, official documents and most written work.

Key rules:
- Subject and verb must agree:
  "She walks" not "she walk".
  "They were" not "they was".

- Use "I" as a subject and "me" as an object:
  "She and I went."
  "She called me."

- Avoid double negatives:
  "I haven't seen anyone" not "I haven't seen no one."

- Use "did", not "done", as the simple past tense:
  "I did it" not "I done it."

Dialect and slang can be valuable in speech, fiction dialogue and personal voice. However, for exams, essays and formal work, Standard English is the safest default.

Register means the level of formality. Strong writers match register to audience and purpose.
`
      },
      interactiveLesson: {
        title: 'Standard English',
        steps: [
          { type: 'question', question: 'Which is Standard English?', options: ['"They were late."', '"They was late."'], correct: 0 },
          { type: 'question', question: 'Which is correct?', options: ['"She and I went home."', '"Me and her went home."'], correct: 0 },
          { type: 'question', question: 'Double negatives are:', options: ['Not Standard English', 'Standard English'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text: "Me and my mate was at the park and I ain't seen no one."

Model answer:
Standard English:
"My friend and I were at the park, and I haven't seen anyone."

Subject pronouns are corrected, subject–verb agreement is fixed, and the double negative is removed.
`
      },
      practice: [
        { type: 'mcq', question: 'Which is correct in Standard English?', options: ['"He did it."', '"He done it."'], correct: 0 },
        { type: 'mcq', question: 'Which is formal?', options: ['"I disagree with this view."', '"Nah, that is rubbish."'], correct: 0 },
        { type: 'true-false', question: 'Slang should usually appear in a formal exam essay.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite in Standard English: "My brother and me ain't done nothing wrong."`,
        sentenceStarter: 'My brother and I...',
        hints: ['Remove the double negative.', 'Use correct pronouns.', 'Use "did" rather than "done".']
      },
      aiFeedback: {
        successCriteria: ['Correct subject pronouns', 'Subject–verb agreement', 'No double negatives', 'Appropriate register']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is dialect valuable in fiction even if it is not Standard English?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 11 — PARAGRAPHS
    // =========================================================
    writingSkill({
      id: 'eng-5-11',
      slug: 'paragraphs',
      title: 'Paragraphs',
      summary: 'When and why to start a new paragraph.',
      keyVocabulary: ['paragraph', 'TIPT', 'topic', 'time', 'place', 'person', 'coherence'],
      examinerTip:
        'Paragraphing is a structure signal. It tells the reader how your ideas or narrative moments are organised.',
      commonMistakes: [
        'Writing one huge block of text.',
        'Starting a new paragraph at random.',
        'Forgetting a new paragraph for a new speaker in dialogue.'
      ],
      grade9Move:
        'Use paragraph length deliberately: longer paragraphs can build detail; a single-sentence paragraph can create shock, contrast or emphasis.',
      explainer: {
        title: 'Paragraphs',
        content: `
A paragraph is a group of sentences about one main idea.

Start a new paragraph when:
- A new Topic begins.
- A new Time begins.
- A new Place appears.
- A new Person speaks.

This is easy to remember with TIPT:
Topic, Time, Place, Person.

A paragraph should:
- Focus on one main idea.
- Flow logically from the one before.
- Help the reader follow the sequence of thought or events.

In formal writing, paragraphs are usually at least 2–3 sentences. In fiction, very short paragraphs can be used for impact.
`
      },
      interactiveLesson: {
        title: 'When to Start a New Paragraph',
        steps: [
          { type: 'question', question: 'You should start a new paragraph when:', options: ['A new topic, time, place or person begins', 'Every 3 words'], correct: 0 },
          { type: 'question', question: 'A paragraph should focus on:', options: ['One main idea', 'Ten different ideas'], correct: 0 },
          { type: 'question', question: 'TIPT stands for:', options: ['Topic, Time, Place, Person', 'Title, Intro, Plan, Task'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text in one block:
"The meeting ended. Later that evening the team gathered at the café. 'We did well today,' said Priya."

Model paragraphing:
Paragraph 1: "The meeting ended."
Paragraph 2: "Later that evening the team gathered at the café."
Paragraph 3: "'We did well today,' said Priya."

Why?
The second paragraph begins a new time and place. The third paragraph begins direct speech by a person.
`
      },
      practice: [
        { type: 'mcq', question: 'A new speaker usually triggers:', options: ['A new paragraph', 'A comma'], correct: 0 },
        { type: 'mcq', question: 'TIPT helps with:', options: ['Paragraphing', 'Apostrophes'], correct: 0 },
        { type: 'true-false', question: 'Paragraphs help the reader follow ideas.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Explain why this block needs to be broken into two paragraphs: "The bell rang. Outside, the playground was quiet."`,
        sentenceStarter: 'It needs two paragraphs because...',
        hints: ['Think TIPT.', 'Look for a change of place or focus.']
      },
      aiFeedback: {
        successCriteria: ['Identifies the new idea or place', 'Explains the TIPT reason', 'Uses clear terminology']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does a one-sentence paragraph sometimes hit harder than a longer one?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 12 — USING PARAGRAPHS
    // =========================================================
    writingSkill({
      id: 'eng-5-12',
      slug: 'using-paragraphs',
      title: 'Using Paragraphs',
      summary: 'Topic sentences and PEE paragraphs.',
      keyVocabulary: ['topic sentence', 'PEE', 'point', 'evidence', 'explain', 'analysis', 'cohesion'],
      examinerTip:
        'A clear topic sentence helps the examiner see your argument immediately. Do not make the marker search for your point.',
      commonMistakes: [
        'Starting with a quotation before making a point.',
        'Giving evidence without explaining it.',
        'Writing a paragraph with several unrelated points.'
      ],
      grade9Move:
        'Move beyond basic PEE by making your explanation layered: word-level analysis, effect on the reader, and link to the writer’s purpose.',
      explainer: {
        title: 'Using Paragraphs',
        content: `
A strong analytical paragraph usually opens with a topic sentence. This is a short, clear sentence that signals the main idea.

For analytical writing, use POINT → EVIDENCE → EXPLAIN:

- Point: make a clear claim.
- Evidence: use a short, relevant quotation or detail.
- Explain: comment on what the evidence shows and why it matters.

Example:
Point — Dickens presents poverty as inescapable.
Evidence — Oliver is described as "covered in rags".
Explain — The word "covered" suggests poverty surrounds him completely; he has no easy escape from it.

Every paragraph should serve the overall argument. If a sentence does not relate to the topic sentence, cut or move it.
`
      },
      interactiveLesson: {
        title: 'PEE Paragraphs',
        steps: [
          { type: 'question', question: 'PEE stands for:', options: ['Point, Evidence, Explain', 'Plan, Edit, End'], correct: 0 },
          { type: 'question', question: 'A strong paragraph often starts with a:', options: ['Topic sentence', 'Long quotation'], correct: 0 },
          { type: 'question', question: 'Every sentence in a paragraph should:', options: ['Serve the main idea', 'Introduce a new topic'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Model paragraph:
"Dickens presents Oliver as vulnerable. He describes him as 'small and pale'. The adjective 'small' emphasises Oliver's youth and fragility, while 'pale' suggests malnutrition; together, they make the reader pity him."

Why it works:
- Clear point.
- Short evidence.
- Word-level explanation.
- Effect on the reader.
`
      },
      practice: [
        { type: 'mcq', question: 'The evidence in PEE is:', options: ['A short, relevant quotation or detail', 'An unsupported opinion'], correct: 0 },
        { type: 'mcq', question: 'A topic sentence should usually:', options: ['Signal the paragraph’s main idea', 'Repeat the whole question word for word'], correct: 0 },
        { type: 'true-false', question: 'Every paragraph should serve the overall argument.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write one PEE paragraph about a character who appears kind. Invent any short quotation.`,
        sentenceStarter: 'The writer presents...',
        hints: ['Start with the point.', 'Add a short quotation.', 'Explain the effect.']
      },
      aiFeedback: {
        successCriteria: ['Clear point', 'Short embedded quote or evidence', 'Explanation of effect', 'Links back to the main idea']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does starting with a topic sentence make your paragraph easier to mark?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 13 — LINKING PARAGRAPHS
    // =========================================================
    writingSkill({
      id: 'eng-5-13',
      slug: 'linking-paragraphs',
      title: 'Linking Paragraphs',
      summary: 'Transitions, connectives and signposts.',
      keyVocabulary: ['transition', 'connective', 'signpost', 'cohesion', 'contrast', 'sequence', 'comparison'],
      examinerTip:
        'Good linking is not just using "Furthermore" at the start of every paragraph. The link should show the relationship between ideas.',
      commonMistakes: [
        'Using the same connective repeatedly.',
        'Using a contrast connective when the ideas do not contrast.',
        'Making paragraphs feel like separate mini-essays.'
      ],
      grade9Move:
        'Use conceptual links, not just connectives: repeat a key word, develop an image, or build from the previous paragraph’s final idea.',
      explainer: {
        title: 'Linking Paragraphs',
        content: `
Paragraphs should not jump. They should flow.

Use connectives to show how ideas relate:

- Adding: "Furthermore," "In addition,"
- Contrasting: "However," "On the other hand,"
- Causing: "As a result," "Therefore,"
- Sequencing: "Firstly," "Next," "Finally,"
- Comparing: "Similarly," "In the same way,"

You can also link paragraphs by:
- Repeating a key word from the previous paragraph.
- Referring back: "Building on this..."
- Signposting: "Another key idea is..."
- Creating a chain of ideas across the essay.

Good linking makes an argument feel connected and controlled.
`
      },
      interactiveLesson: {
        title: 'Connectives',
        steps: [
          { type: 'question', question: '"However" is used to:', options: ['Contrast', 'Sequence'], correct: 0 },
          { type: 'question', question: '"As a result" is used to show:', options: ['Cause', 'Addition'], correct: 0 },
          { type: 'question', question: 'Repeating a key word between paragraphs creates:', options: ['Linkage', 'Confusion'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"The opening presents fear. The ending presents hope."

Model answer:
"The opening presents fear. However, the ending presents hope, suggesting..."

The connective "However" marks the shift clearly.
`
      },
      practice: [
        { type: 'mcq', question: '"Furthermore" means:', options: ['Adding another point', 'Changing topic completely'], correct: 0 },
        { type: 'mcq', question: '"By contrast" signals:', options: ['Difference', 'Cause'], correct: 0 },
        { type: 'true-false', question: 'Every paragraph transition needs an obvious connective.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite these two sentences with a linking word between them: "The character seems brave. The character also has doubts."`,
        sentenceStarter: 'The character seems brave. However,...',
        hints: ['Use a contrast connective.', 'Make the second sentence flow naturally.']
      },
      aiFeedback: {
        successCriteria: ['Uses an appropriate connective or linking phrase', 'Meaning preserved', 'Relationship between ideas is clear']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is repeating a keyword across paragraphs sometimes more powerful than using a connective?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 14 — MAKING YOUR WRITING INTERESTING
    // =========================================================
    writingSkill({
      id: 'eng-5-14',
      slug: 'making-your-writing-interesting',
      title: 'Making Your Writing Interesting',
      summary: 'Variety, voice and technique.',
      keyVocabulary: ['voice', 'imagery', 'show don’t tell', 'simile', 'metaphor', 'rhetorical question', 'sentence variety'],
      examinerTip:
        'Interesting writing is not the same as over-decorated writing. The best writing uses techniques because they suit the purpose.',
      commonMistakes: [
        'Adding too many adjectives.',
        'Using random metaphors that do not fit the mood.',
        'Telling the emotion instead of showing it through action.'
      ],
      grade9Move:
        'Create a distinctive voice through precise verbs, controlled sentence rhythm and details that reveal mood indirectly.',
      explainer: {
        title: 'Making Your Writing Interesting',
        content: `
Interesting writing has variety and control.

Ways to add variety:
- Mix sentence types: simple, compound and complex.
- Vary sentence lengths: short for impact, longer for flow.
- Use a strong voice: let personality show through word choice and rhythm.
- Use literary techniques: similes, metaphors, imagery, rhetorical questions and personification.
- Replace common words with stronger ones:
  "said" → "whispered", "snapped", "murmured"
  "walked" → "stumbled", "strode", "crept"
- Show rather than tell:
  "Her hands shook" is stronger than "She was nervous."

Cut anything that does not add meaning, mood or personality. Strong writing is usually tighter.
`
      },
      interactiveLesson: {
        title: 'Making Writing Interesting',
        steps: [
          { type: 'question', question: '"Show don\'t tell" means:', options: ['Describe behaviour rather than simply naming the feeling', 'Draw pictures'], correct: 0 },
          { type: 'question', question: '"Whispered" is usually more specific than:', options: ['Said', 'Snapped'], correct: 0 },
          { type: 'question', question: 'Variety means mixing:', options: ['Sentence types and lengths', 'Fonts'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"She was angry. She told them she was angry."

Model answer:
"Her jaw tightened. 'That,' she said, her voice low, 'is unacceptable.'"

The new version shows anger through body language and controlled speech rather than simply naming the emotion.
`
      },
      practice: [
        { type: 'mcq', question: 'Which is more vivid?', options: ['"Her hands shook."', '"She was nervous."'], correct: 0 },
        { type: 'mcq', question: 'Which verb suggests confident movement?', options: ['strode', 'wandered'], correct: 0 },
        { type: 'true-false', question: 'Cutting unnecessary words usually improves writing.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite this dull sentence to make it more interesting: "He was sad and walked away."`,
        sentenceStarter: '',
        hints: ['Replace "sad" with action or image.', 'Use a stronger verb than "walked".']
      },
      aiFeedback: {
        successCriteria: ['Shows not tells', 'Uses vivid vocabulary', 'Keeps meaning', 'Avoids over-writing']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does overusing literary techniques weaken writing?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 15 — USING ADJECTIVES AND COMPARISONS
    // =========================================================
    writingSkill({
      id: 'eng-5-15',
      slug: 'using-adjectives-and-comparisons',
      title: 'Using Adjectives and Comparisons',
      summary: 'Strong adjectives, similes and metaphors.',
      keyVocabulary: ['adjective', 'simile', 'metaphor', 'personification', 'comparison', 'cliche', 'precision'],
      examinerTip:
        'One precise adjective is usually stronger than three predictable ones.',
      commonMistakes: [
        'Stacking adjectives without improving the image.',
        'Using cliches such as "white as snow".',
        'Mixing comparisons that do not belong together.'
      ],
      grade9Move:
        'Use comparisons to deepen meaning, not just decorate description. A fresh comparison should reveal mood, character or theme.',
      explainer: {
        title: 'Using Adjectives and Comparisons',
        content: `
Adjectives describe nouns. Strong adjectives paint a clearer picture.

- Weak: "a big house"
- Strong: "a cavernous house"

Avoid overusing adjectives. A single precise adjective is usually stronger than two or three ordinary ones.

Comparisons bring descriptions to life:
- Simile: compares using "like" or "as". "Her voice was as soft as velvet."
- Metaphor: says one thing is another. "Her voice was velvet."
- Personification: gives human qualities to non-human things. "The branches reached for the sky."

Good comparisons feel fresh. Avoid tired ones like "white as snow" unless you twist them:
"white as hospital sheets"
`
      },
      interactiveLesson: {
        title: 'Adjectives and Comparisons',
        steps: [
          { type: 'question', question: '"Cavernous" is:', options: ['A stronger version of "big"', 'A weaker version of "big"'], correct: 0 },
          { type: 'question', question: '"Her voice was velvet" is a:', options: ['Metaphor', 'Simile'], correct: 0 },
          { type: 'question', question: '"The branches reached for the sky" is:', options: ['Personification', 'Simile'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"The big, loud, busy market was nice."

Model answer:
"The market roared like a living thing."

One vivid comparison replaces several dull adjectives and creates a stronger sense of energy.
`
      },
      practice: [
        { type: 'mcq', question: 'A simile uses:', options: ['"like" or "as"', 'a full stop'], correct: 0 },
        { type: 'mcq', question: 'Which is more precise?', options: ['cavernous', 'big'], correct: 0 },
        { type: 'true-false', question: 'Stacking adjectives always makes writing stronger.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Replace the weak adjectives with stronger writing: "a big nice old house".`,
        sentenceStarter: '',
        hints: ['Pick one precise adjective.', 'Or use a comparison.', 'Avoid a list of bland adjectives.']
      },
      aiFeedback: {
        successCriteria: ['Strong or precise adjective chosen', 'Comparison is fresh if used', 'Meaning preserved']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why are fresh, unusual comparisons remembered more than common ones?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 16 — USING IMPRESSIVE VOCABULARY
    // =========================================================
    writingSkill({
      id: 'eng-5-16',
      slug: 'using-impressive-vocabulary',
      title: 'Using Impressive Vocabulary',
      summary: 'Choose precise words and avoid repetition.',
      keyVocabulary: ['vocabulary', 'precision', 'connotation', 'synonym', 'verb', 'noun', 'intensity'],
      examinerTip:
        'Impressive vocabulary means precise vocabulary. Long words used wrongly do more harm than simple words used accurately.',
      commonMistakes: [
        'Using a thesaurus word without checking meaning.',
        'Repeating the same adjective too often.',
        'Choosing long words that do not fit the tone.'
      ],
      grade9Move:
        'Build meaning through strong nouns and verbs first. Then use adjectives and adverbs only when they add something specific.',
      explainer: {
        title: 'Using Impressive Vocabulary',
        content: `
Impressive vocabulary is precise, not showy.

Rules:
- Pick the right word, not the longest word.
- "Annoyed", "irritated" and "furious" all suggest anger, but at different intensities.
- Use a thesaurus wisely: check meaning, tone and connotation before swapping a word.
- Avoid repeating the same word in quick succession.

Collect strong verbs and nouns:
- "Walked" → strode, crept, stumbled, marched.
- "House" → cottage, mansion, shack, manor.
- "Looked" → glanced, stared, studied, glared.

Impressive writing usually has strong nouns and verbs. Adjectives and adverbs come second.
`
      },
      interactiveLesson: {
        title: 'Better Vocabulary',
        steps: [
          { type: 'question', question: 'Impressive vocabulary is:', options: ['Precise', 'Long'], correct: 0 },
          { type: 'question', question: '"Crept" replaces "walked" when:', options: ['The character is moving quietly', 'The character is sprinting'], correct: 0 },
          { type: 'question', question: 'Which kind of words usually matter most?', options: ['Strong verbs and nouns', 'Random adverbs'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"The man walked into the big house. The big house was nice."

Model answer:
"The man crept into the manor. The hallway glowed with candlelight."

The stronger verb "crept" and precise noun "manor" do more work than stacked adjectives.
`
      },
      practice: [
        { type: 'mcq', question: 'Which is most precise?', options: ['"Furious"', '"Bad"'], correct: 0 },
        { type: 'mcq', question: 'Which verb suggests anger?', options: ['glared', 'glanced'], correct: 0 },
        { type: 'true-false', question: 'Using long words always makes writing better.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite this using stronger vocabulary: "She walked into the room and looked at the big box."`,
        sentenceStarter: '',
        hints: ['Replace "walked" with something specific.', 'Replace "looked" with something specific.', 'Replace "big box" with a more precise noun phrase.']
      },
      aiFeedback: {
        successCriteria: ['Precise verb', 'Precise adjective or noun', 'Meaning preserved', 'Tone fits the sentence']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why can overusing rare words weaken writing instead of strengthening it?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 17 — VARYING SENTENCE LENGTH
    // =========================================================
    writingSkill({
      id: 'eng-5-17',
      slug: 'varying-sentence-length',
      title: 'Varying Sentence Length',
      summary: 'Short for impact, long for flow.',
      keyVocabulary: ['sentence length', 'pace', 'rhythm', 'emphasis', 'long sentence', 'short sentence', 'fragment'],
      examinerTip:
        'Sentence length controls pace. A short sentence only has impact if the surrounding rhythm gives it space.',
      commonMistakes: [
        'Using lots of short sentences until the writing feels childish.',
        'Writing long sentences that lose grammatical control.',
        'Forgetting that sentence fragments should be deliberate, not accidental.'
      ],
      grade9Move:
        'Use rhythm patterns such as long-long-short or short-long-short to control tension, atmosphere and emphasis.',
      explainer: {
        title: 'Varying Sentence Length',
        content: `
Varying sentence length controls pace and feeling.

- Short sentences: create tension, shock or emphasis.
- Medium sentences: carry the main argument smoothly.
- Long sentences: build atmosphere, pile up detail or reflect flowing thought.

A classic rhythm is long–long–short:
"The fog had rolled in from the sea and wrapped itself around every lamppost. The whole street was quiet, muffled, strange. Then: a door slammed."

Short sentences lose power if you use them too often. Vary deliberately.
`
      },
      interactiveLesson: {
        title: 'Sentence Length',
        steps: [
          { type: 'question', question: 'Short sentences usually create:', options: ['Tension or emphasis', 'Calm flow only'], correct: 0 },
          { type: 'question', question: 'Long sentences usually create:', options: ['Flow or atmosphere', 'Only shock'], correct: 0 },
          { type: 'question', question: 'A long–long–short pattern:', options: ['Ends with punch', 'Ends with an adjective'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"It was quiet. It was dark. It was cold. No one moved."

Model answer:
"The night was quiet, dark and cold, and somewhere far below the city kept sleeping. No one moved."

The mix of a longer flowing sentence and a sudden short one creates more controlled tension.
`
      },
      practice: [
        { type: 'mcq', question: '"She knew." is an example of:', options: ['A short sentence', 'A long sentence'], correct: 0 },
        { type: 'mcq', question: 'Long sentences are useful for:', options: ['Building atmosphere', 'Always creating shock'], correct: 0 },
        { type: 'true-false', question: 'Varying sentence length improves pace.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write three sentences describing a tense moment. Vary the lengths deliberately.`,
        sentenceStarter: '',
        hints: ['Try ending with a short, sharp sentence.', 'Keep grammar controlled.']
      },
      aiFeedback: {
        successCriteria: ['Three sentences of different lengths', 'Clear rhythm', 'Tension maintained', 'No accidental fragments']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does a single short sentence lose impact if it comes after ten other short sentences?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 18 — ESSAYS: THE BASICS
    // =========================================================
    writingSkill({
      id: 'eng-5-18',
      slug: 'essays-the-basics',
      title: 'Essays: The Basics',
      summary: 'Introduction, body, conclusion.',
      keyVocabulary: ['essay', 'introduction', 'thesis', 'body paragraph', 'conclusion', 'argument', 'discursive'],
      examinerTip:
        'An essay is not just information. It is organised argument. Every paragraph should help answer the question.',
      commonMistakes: [
        'Writing an introduction that is too vague.',
        'Adding evidence without linking it to the argument.',
        'Introducing a brand new idea in the conclusion.'
      ],
      grade9Move:
        'Write a thesis that gives a clear line of argument, not just a topic. Then make every paragraph develop that line.',
      explainer: {
        title: 'Essays: The Basics',
        content: `
An essay is a structured piece of writing that argues, explores or explains a point.

A basic essay has:
- Introduction: introduces the topic and states your main idea, often called a thesis.
- Body paragraphs: each develops one main point with evidence and explanation.
- Conclusion: sums up your argument and leaves the reader with a final thought.

Different essay types:
- Literature essay: analyse a text, use quotations, link to writer's methods and context.
- Language response: analyse how a writer uses language and structure.
- Transactional or discursive writing: argue, persuade, explain or inform for a specific audience.

Rules:
- Every paragraph should serve the overall argument.
- Every point needs support.
- The essay should have a clear logical order.
`
      },
      interactiveLesson: {
        title: 'Essay Parts',
        steps: [
          { type: 'question', question: 'Which part states your main idea?', options: ['Introduction', 'Random middle paragraph'], correct: 0 },
          { type: 'question', question: 'Each body paragraph should develop:', options: ['One main point', 'All your points at once'], correct: 0 },
          { type: 'question', question: 'A discursive essay:', options: ['Discusses an idea, often from more than one side', 'Only describes a room'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Structure of a short essay on persuasion in speeches:

- Introduction: states that speeches persuade through logic, emotion and rhythm.
- Body 1: logic — facts, evidence and reasoning.
- Body 2: emotion — emotive language and anecdotes.
- Body 3: rhythm — repetition and lists of three.
- Conclusion: explains how all three work together to make speeches powerful.
`
      },
      practice: [
        { type: 'mcq', question: 'A thesis is:', options: ['Your main argument', 'A long quotation'], correct: 0 },
        { type: 'mcq', question: 'A body paragraph should usually contain:', options: ['A point, support and explanation', 'Only a quote'], correct: 0 },
        { type: 'true-false', question: 'A conclusion should introduce a completely new main idea.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Plan a short essay on why reading is important.`,
        sentenceStarter: 'Introduction: ...\nBody 1: ...\nBody 2: ...\nBody 3: ...\nConclusion: ...',
        hints: ['Each body paragraph should be one idea.', 'Make body topics different from each other.']
      },
      aiFeedback: {
        successCriteria: ['Clear introduction', 'Three distinct body topics', 'Clear conclusion', 'Logical order']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does a strong conclusion matter as much as a strong introduction?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 19 — PLANNING ESSAYS
    // =========================================================
    writingSkill({
      id: 'eng-5-19',
      slug: 'planning-essays',
      title: 'Planning Essays',
      summary: 'Brainstorm, outline, thesis.',
      keyVocabulary: ['plan', 'brainstorm', 'outline', 'thesis', 'evidence', 'logical order', 'focus words'],
      examinerTip:
        'A plan should prevent drift. It is better to plan three strong points than write five weak, repetitive paragraphs.',
      commonMistakes: [
        'Ignoring the exact wording of the question.',
        'Planning too much and leaving too little writing time.',
        'Choosing points before brainstorming enough options.'
      ],
      grade9Move:
        'Order your points strategically: build towards the most complex or most convincing argument.',
      explainer: {
        title: 'Planning Essays',
        content: `
Planning takes a few minutes but saves time during writing.

Steps:
1. Read the question carefully. Underline the focus words.
2. Brainstorm. Jot down everything useful: ideas, quotes, techniques, context.
3. Choose your strongest 2–4 points.
4. Order them logically. Often, go from simpler to more complex or weakest to strongest.
5. Write a thesis: one sentence that sums up your argument.
6. Outline each paragraph's topic sentence and key evidence.

A plan is just notes for yourself. It does not need full sentences. Keep it quick and useful.
`
      },
      interactiveLesson: {
        title: 'Planning',
        steps: [
          { type: 'question', question: 'The first step in planning is:', options: ['Read the question carefully', 'Start writing immediately'], correct: 0 },
          { type: 'question', question: 'A thesis is:', options: ['One sentence summarising your argument', 'A full paragraph'], correct: 0 },
          { type: 'question', question: 'Why might you end with your strongest point?', options: ['It leaves the strongest impression', 'It is always the shortest'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does the writer create fear in this passage?

Plan:
- Thesis: fear is built through sound, sentence length and imagery.
- Point 1: Sound — "creak", "whisper" — eerie atmosphere.
- Point 2: Sentence length — very short sentences create tension.
- Point 3: Imagery — "shadow like a hand" — threatening and claustrophobic.
`
      },
      practice: [
        { type: 'mcq', question: 'A plan should be:', options: ['Quick notes', 'A polished paragraph'], correct: 0 },
        { type: 'mcq', question: 'Focus words are:', options: ['The key words in the question', 'Random impressive words'], correct: 0 },
        { type: 'true-false', question: 'Planning always wastes time.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write a short plan for this essay: "Why is reading fiction important?"`,
        sentenceStarter: 'Thesis: ...\nPoint 1: ...\nPoint 2: ...\nPoint 3: ...',
        hints: ['Keep each line short.', 'End with the strongest point.']
      },
      aiFeedback: {
        successCriteria: ['Clear thesis', 'Three distinct points', 'Logical order', 'Relevant to the question']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is brainstorming before choosing your points stronger than picking the first idea you think of?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 20 — WRITING ESSAYS
    // =========================================================
    writingSkill({
      id: 'eng-5-20',
      slug: 'writing-essays',
      title: 'Writing Essays',
      summary: 'Turn a plan into fluent paragraphs.',
      keyVocabulary: ['thesis', 'topic sentence', 'evidence', 'explanation', 'conclusion', 'coherence', 'argument'],
      examinerTip:
        'Keep answering the question. A fluent essay that drifts away from the task will not score as highly as a focused one.',
      commonMistakes: [
        'Copying the plan rather than developing it.',
        'Forcing in a quotation that does not fit.',
        'Using connectives mechanically without developing argument.'
      ],
      grade9Move:
        'Make every paragraph echo the thesis in a slightly different way, so the essay feels unified rather than repetitive.',
      explainer: {
        title: 'Writing Essays',
        content: `
When turning a plan into an essay:

- Open with a sharp introduction. State your thesis clearly.
- Use your plan as a guide: one body paragraph per point.
- Start each body paragraph with a topic sentence that links to the question.
- Use point, evidence and explanation.
- Finish with a conclusion that sums up your argument and offers a final thought.

Useful openers:
- "This essay will argue that..."
- "Firstly, the writer uses..."
- "In addition,..."
- "However,..."
- "In conclusion,..."

Keep sentences clear. Avoid over-long sentences that lose control. Focus on answering the question at all times.
`
      },
      interactiveLesson: {
        title: 'Turning Plan into Essay',
        steps: [
          { type: 'question', question: 'Every body paragraph should start with:', options: ['A topic sentence', 'A long quotation'], correct: 0 },
          { type: 'question', question: '"However" is useful for:', options: ['Contrast', 'Listing only'], correct: 0 },
          { type: 'question', question: 'A conclusion should:', options: ['Sum up and offer a final thought', 'Introduce a brand new quotation'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Plan point:
Sound creates fear.

Written paragraph:
"Firstly, the writer creates fear through sound. The words 'creak' and 'whisper' appear repeatedly, imitating the noises of a house where something is hidden. The reader listens as closely as the characters, which builds shared dread."

Why it works:
The paragraph turns one plan note into a developed analytical point.
`
      },
      practice: [
        { type: 'mcq', question: 'An essay should always:', options: ['Answer the question', 'Copy the plan word for word'], correct: 0 },
        { type: 'mcq', question: 'A forced quotation is one that:', options: ['Does not really support the point', 'Is short and relevant'], correct: 0 },
        { type: 'true-false', question: 'A topic sentence opens each body paragraph clearly.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Using a plan point ("reading builds empathy"), write one body paragraph.`,
        sentenceStarter: 'Firstly, reading fiction builds empathy because...',
        hints: ['Make a point.', 'Add an example.', 'Explain the effect.']
      },
      aiFeedback: {
        successCriteria: ['Clear topic sentence', 'Supporting example', 'Explanation', 'Links back to the question']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is it better to cut a great quotation that does not fit than to force it into your essay?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 21 — STRUCTURING ESSAYS
    // =========================================================
    writingSkill({
      id: 'eng-5-21',
      slug: 'structuring-essays',
      title: 'Structuring Essays',
      summary: 'Chronological, thematic and comparison structures.',
      keyVocabulary: ['chronological', 'thematic', 'comparison', 'point-by-point', 'text-by-text', 'structure', 'line of argument'],
      examinerTip:
        'The structure should fit the question. Do not automatically write chronologically just because the text has a beginning, middle and end.',
      commonMistakes: [
        'Using text-by-text comparison and forgetting to compare.',
        'Writing chronologically when the question asks about a theme.',
        'Changing structure halfway through.'
      ],
      grade9Move:
        'Choose a structure that creates argument: organise by the ideas the question demands, not by the order you remembered them.',
      explainer: {
        title: 'Structuring Essays',
        content: `
Not all essays are structured the same way. Match the structure to the question.

Three common structures:

1. Chronological:
Organise by time order: start, middle, end of the text.
Useful for: "How does the character change over time?"

2. Thematic:
Organise by ideas or themes, not time.
Useful for: "How does the writer present love?"

3. Comparison:
Organise by similarities and differences between two texts.
Useful for: "Compare text A and text B."

For comparison essays, two structures are common:
- Point-by-point: every paragraph compares both texts on one idea. This is usually stronger.
- Text-by-text: first half on text A, second half on text B. This can become less comparative.

Pick the structure before you start writing.
`
      },
      interactiveLesson: {
        title: 'Essay Structures',
        steps: [
          { type: 'question', question: 'A chronological structure follows:', options: ['Time order', 'Random order'], correct: 0 },
          { type: 'question', question: 'A "How does the writer present love?" essay is likely to be:', options: ['Thematic', 'Chronological only'], correct: 0 },
          { type: 'question', question: 'For comparing two texts, the stronger structure is usually:', options: ['Point-by-point', 'Text-by-text'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does Macbeth change over the play?

Structure:
Chronological.

- Paragraph 1: Act 1 — loyal soldier.
- Paragraph 2: Act 2–3 — murderer and paranoid king.
- Paragraph 3: Act 4–5 — tyrant, isolated and defeated.

This works because the question asks about change over time.
`
      },
      practice: [
        { type: 'mcq', question: 'A comparison essay should ideally compare:', options: ['Point-by-point', 'Only text-by-text'], correct: 0 },
        { type: 'mcq', question: 'A thematic structure organises by:', options: ['Ideas', 'Page number only'], correct: 0 },
        { type: 'true-false', question: 'The structure should match the question.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Pick a structure (chronological, thematic, or comparison) for this question: "How does the writer present family in this novel?"`,
        sentenceStarter: 'I would use...',
        hints: ['Think about what the question asks.', 'Family is a theme, so consider a thematic structure.']
      },
      aiFeedback: {
        successCriteria: ['Picks an appropriate structure', 'Justifies the choice', 'Links structure to the question']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `When would a chronological structure weaken an analytical essay?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 22 — DRAFTING AND EDITING ESSAYS
    // =========================================================
    writingSkill({
      id: 'eng-5-22',
      slug: 'drafting-and-editing-essays',
      title: 'Drafting and Editing Essays',
      summary: 'Write, then revise.',
      keyVocabulary: ['draft', 'edit', 'proofread', 'revise', 'coherence', 'clarity', 'impact'],
      examinerTip:
        'Editing is not just correcting mistakes. It is improving meaning, precision, structure and impact.',
      commonMistakes: [
        'Thinking the first version is finished.',
        'Only checking spelling and ignoring clarity.',
        'Adding more words instead of cutting weak ones.'
      ],
      grade9Move:
        'Edit in passes: first argument and structure, then paragraph clarity, then sentence precision, then punctuation and spelling.',
      explainer: {
        title: 'Drafting and Editing Essays',
        content: `
Great essays are usually not written perfectly in one go.

Stages:
- Draft: get the full essay down without worrying about perfection.
- Edit: sharpen meaning. Cut weak sentences. Strengthen topic sentences.
- Proofread: check spelling, punctuation and grammar.

When editing, ask:
- Does every paragraph answer the question?
- Is the evidence specific?
- Is the explanation developed?
- Are there words to cut?
- Does the conclusion leave the reader with a clear final thought?

In exams, you may not have time for a full redraft, but you can and should re-read and tidy up.
`
      },
      interactiveLesson: {
        title: 'Drafting and Editing',
        steps: [
          { type: 'question', question: 'Drafting means:', options: ['Writing a first version', 'Rewriting from scratch every time'], correct: 0 },
          { type: 'question', question: 'Words often cut in editing include:', options: ['"Very" and "really"', '"He" and "she"'], correct: 0 },
          { type: 'question', question: 'Proofreading checks:', options: ['Spelling, punctuation and grammar', 'Only essay structure'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Draft:
"The writer very clearly uses really emotive language that is quite powerful."

Edited:
"The writer uses emotive language powerfully."

Cutting "very", "really" and "quite" makes the sentence sharper.
`
      },
      practice: [
        { type: 'mcq', question: 'Which word is often cuttable?', options: ['"Very"', '"Writer"'], correct: 0 },
        { type: 'mcq', question: 'Editing should improve:', options: ['Clarity and impact', 'Word count only'], correct: 0 },
        { type: 'true-false', question: 'Even in exams, re-reading your work can improve it.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Edit this to make it sharper: "The writer really uses a very powerful simile that is quite effective."`,
        sentenceStarter: 'The writer uses a powerful simile that...',
        hints: ['Cut unnecessary intensifiers.', 'Keep the meaning but sharpen the phrasing.']
      },
      aiFeedback: {
        successCriteria: ['Cuts weak intensifiers', 'Sharper sentence', 'Meaning preserved', 'Improved clarity']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does cutting words usually make writing stronger, not weaker?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 23 — USING EXAMPLES
    // =========================================================
    writingSkill({
      id: 'eng-5-23',
      slug: 'using-examples',
      title: 'Using Examples',
      summary: 'Back up every claim with specific evidence.',
      keyVocabulary: ['example', 'evidence', 'claim', 'specific', 'support', 'detail', 'statistic'],
      examinerTip:
        'A claim without evidence is just an assertion. Specific support makes the argument credible.',
      commonMistakes: [
        'Using vague phrases such as "many people think".',
        'Giving examples that do not prove the point.',
        'Using too many weak examples instead of one strong one.'
      ],
      grade9Move:
        'Choose examples that do double work: they support the claim and allow you to analyse language, structure, context or effect.',
      explainer: {
        title: 'Using Examples',
        content: `
Every claim in your writing should be backed up with specific examples.

Weak:
"The writer uses language."

Strong:
"The writer uses the metaphor 'her heart was a drum'."

Good examples are:
- Short
- Specific
- Clearly linked to the point

For non-fiction, examples can include:
- Real statistics
- Named people, events or places
- Short quotations
- Specific facts

Avoid general claims. "Many people think..." is weaker than a precise example or statistic.
`
      },
      interactiveLesson: {
        title: 'Using Examples',
        steps: [
          { type: 'question', question: 'Which is stronger evidence?', options: ['"A survey found 62%..."', '"Many people think..."'], correct: 0 },
          { type: 'question', question: 'Examples should be:', options: ['Short and specific', 'Long and vague'], correct: 0 },
          { type: 'question', question: 'Every claim should be backed by:', options: ['Evidence', 'A random opinion'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Claim:
"The character is sad."

Weak version:
"The character is sad all the time."

Strong version:
"The character sits alone by the window for hours, staring at the rain."

The strong version shows the sadness with a specific example.
`
      },
      practice: [
        { type: 'mcq', question: 'Specific examples help because they:', options: ['Show the reader the idea', 'Only add word count'], correct: 0 },
        { type: 'mcq', question: 'Which is more specific?', options: ['"The word \'hollow\' suggests emptiness."', '"The writer uses words."'], correct: 0 },
        { type: 'true-false', question: 'General claims are stronger than specific ones.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Support this claim with a specific example: "Reading helps you learn new words."`,
        sentenceStarter: 'For example,...',
        hints: ['Invent a specific book or word if needed.', 'Explain how the example supports the claim.']
      },
      aiFeedback: {
        successCriteria: ['Specific example', 'Clearly linked to the claim', 'Explanation included']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does one strong example often beat five weak ones?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 24 — USING QUOTES
    // =========================================================
    writingSkill({
      id: 'eng-5-24',
      slug: 'using-quotes',
      title: 'Using Quotes',
      summary: 'Short, embedded and accurate.',
      keyVocabulary: ['quotation', 'embedded quotation', 'inverted commas', 'evidence', 'accuracy', 'word-level analysis'],
      examinerTip:
        'Do not quote a whole sentence if one word or phrase is enough. Examiners reward precise selection and explanation, not copying.',
      commonMistakes: [
        'Using long quotations without selecting the key words.',
        'Copying a quote inaccurately.',
        'Dropping a quote into a paragraph without explaining it.'
      ],
      grade9Move:
        'Embed a short quote inside your own sentence, then zoom in on one word and explain its connotations.',
      explainer: {
        title: 'Using Quotes',
        content: `
Quotations show you understand the text.

Rules:
- Keep them short, often 1–5 words.
- Embed them inside your own sentence.
- Copy accurately.
- Use inverted commas.
- Introduce the quotation smoothly.

Weak:
"The writer says 'his eyes were dark and tired and old' which shows tiredness."

Strong:
"The phrase 'dark and tired' suggests a weariness that goes beyond a sleepless night."

In exams, use short quotations to support points about language, structure, character or theme.
`
      },
      interactiveLesson: {
        title: 'Using Quotes',
        steps: [
          { type: 'question', question: 'Short, embedded quotations are:', options: ['Stronger than long copied chunks', 'Always weaker'], correct: 0 },
          { type: 'question', question: 'Copying a quote incorrectly:', options: ['Weakens your answer', 'Does not matter'], correct: 0 },
          { type: 'question', question: 'Quotations should be:', options: ['Enclosed in inverted commas', 'Underlined only'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Weak:
'The writer says "the dark, damp, cold, empty house stood on the hill" and this creates atmosphere.'

Strong:
'The phrase "dark, damp, cold" creates a layered sense of decay.'

The strong version selects only the most relevant words.
`
      },
      practice: [
        { type: 'mcq', question: 'A strong quotation is:', options: ['Short and relevant', 'Long and full'], correct: 0 },
        { type: 'mcq', question: 'An embedded quote is:', options: ['Built into your own sentence', 'Copied as a separate paragraph'], correct: 0 },
        { type: 'true-false', question: 'Quotations should always be accurate.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Embed this quote smoothly: "The writer says 'thunderous silence'."`,
        sentenceStarter: 'The oxymoron "thunderous silence"...',
        hints: ['Drop the quote into your sentence.', 'Avoid "this quote shows" if you can be smoother.']
      },
      aiFeedback: {
        successCriteria: ['Embedded quotation', 'Short and relevant', 'Accurate punctuation', 'Clear link to point']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is one perfectly chosen short quote usually more powerful than a long copied passage?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 25 — EXPLAINING QUOTES
    // =========================================================
    writingSkill({
      id: 'eng-5-25',
      slug: 'explaining-quotes',
      title: 'Explaining Quotes',
      summary: 'Zoom in on language and link to purpose.',
      keyVocabulary: ['analysis', 'connotation', 'technique', 'effect', 'writer’s purpose', 'zoom in', 'interpretation'],
      examinerTip:
        'A quote does not analyse itself. The marks come from what you do with the quotation.',
      commonMistakes: [
        'Repeating the quote instead of explaining it.',
        'Naming a technique but not explaining its effect.',
        'Forgetting to link the evidence to the writer’s purpose.'
      ],
      grade9Move:
        'Build a chain: technique → key word → connotations → reader effect → writer’s purpose → alternative interpretation.',
      explainer: {
        title: 'Explaining Quotes',
        content: `
Dropping in a quote is not enough. You must explain it.

Strong explanations:
- Name a technique where useful.
- Zoom in on a single word.
- Comment on connotation: the feelings and associations a word carries.
- Link to the writer's bigger purpose.
- Consider more than one possible interpretation.

Example:
Quote: "Her voice was ice."

Explanation:
The metaphor of "ice" shows her tone is not only cold but dangerous. Ice can freeze, shatter and wound, so the image makes the character feel controlled and threatening rather than simply angry.

Great analysis answers:
Why has the writer chosen this word, image or structure?
`
      },
      interactiveLesson: {
        title: 'Explaining Quotes',
        steps: [
          { type: 'question', question: 'Connotation means:', options: ['The feelings and associations a word carries', 'The dictionary meaning only'], correct: 0 },
          { type: 'question', question: 'Strong explanation includes:', options: ['Naming and explaining a technique', 'Just repeating the quote'], correct: 0 },
          { type: 'question', question: 'Great analysis answers:', options: ['Why the writer chose these words', 'How long the text is'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Quote:
"The clouds pressed down like a hand."

Explanation:
The simile "like a hand" personifies the clouds as something alive and heavy. The verb "pressed" adds threat, as if the sky is actively pushing down on the character. This creates a claustrophobic atmosphere and supports the writer's purpose of making the scene feel trapped.
`
      },
      practice: [
        { type: 'mcq', question: 'Analysing connotation means asking:', options: ['What feelings a word carries', 'Who printed the word'], correct: 0 },
        { type: 'mcq', question: 'Which is stronger?', options: ['"The verb \'pressed\' suggests pressure and threat."', '"This quote is good."'], correct: 0 },
        { type: 'true-false', question: 'Zooming in on one word can be more powerful than quoting many.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Explain the quote "her eyes were stones" in 3–4 sentences.`,
        sentenceStarter: 'The metaphor of "stones"...',
        hints: ['Name the technique.', 'Zoom in on "stones".', 'Link to emotion, character or purpose.']
      },
      aiFeedback: {
        successCriteria: ['Names technique', 'Zooms in on a word', 'Explores connotation', 'Links to effect or purpose']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why do examiners reward "why" answers more than "what" answers?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 26 — COMPARING TEXTS
    // =========================================================
    writingSkill({
      id: 'eng-5-26',
      slug: 'comparing-texts',
      title: 'Comparing Texts',
      summary: 'Similarities, differences and signpost language.',
      keyVocabulary: ['comparison', 'similarity', 'difference', 'whereas', 'similarly', 'by contrast', 'point of comparison'],
      examinerTip:
        'Comparison should be woven through the paragraph. Do not write about Text A, then Text B, then add "they are similar" at the end.',
      commonMistakes: [
        'Using evidence from only one text.',
        'Comparing vaguely without a clear point of comparison.',
        'Writing two separate mini-essays.'
      ],
      grade9Move:
        'Compare methods and purposes: show not only that both writers use a technique, but how and why they use it differently.',
      explainer: {
        title: 'Comparing Texts',
        content: `
Comparison means examining two or more texts side by side to show similarities and differences.

Strong comparisons:
- Choose a point of comparison for each paragraph.
- Use signpost language:
  Similarity: "Both writers...", "Similarly,...", "In the same way,..."
  Difference: "Whereas A...", "By contrast,...", "However,..."
- Back every claim with short evidence from both texts.
- Consider how and why each writer makes their choices.

Avoid lumping texts together.

Stronger:
"Text A does X, and similarly Text B does Y; however, the effect is different because..."
`
      },
      interactiveLesson: {
        title: 'Comparing',
        steps: [
          { type: 'question', question: 'Which phrase introduces a similarity?', options: ['"Both writers..."', '"By contrast,..."'], correct: 0 },
          { type: 'question', question: 'Which introduces a difference?', options: ['"Whereas A..."', '"Similarly,..."'], correct: 0 },
          { type: 'question', question: 'Each comparison paragraph should focus on:', options: ['One point of comparison', 'Every detail'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Point of comparison:
Opening tone.

"Both writers open with imagery of darkness. However, Text A uses the word 'shadow' to create mystery, whereas Text B uses 'pitch' to create outright fear. This difference shows how the same technique can produce different moods."
`
      },
      practice: [
        { type: 'mcq', question: '"Whereas" is used to:', options: ['Contrast', 'Agree'], correct: 0 },
        { type: 'mcq', question: 'A strong comparison uses evidence from:', options: ['Both texts', 'Only the first text'], correct: 0 },
        { type: 'true-false', question: 'Every comparison claim needs evidence from both texts.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Compare the opening lines of two texts you know. Focus on one difference.`,
        sentenceStarter: 'Both writers open with...',
        hints: ['Quote briefly from each.', 'Use "whereas" or "however".']
      },
      aiFeedback: {
        successCriteria: ['Quotes or references both texts', 'Uses signpost language', 'Clear point of comparison', 'Explains effect']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is point-by-point comparison usually stronger than writing about each text separately?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 27 — EVALUATING TEXTS
    // =========================================================
    writingSkill({
      id: 'eng-5-27',
      slug: 'evaluating-texts',
      title: 'Evaluating Texts',
      summary: 'Judge how effective a text is and justify your view.',
      keyVocabulary: ['evaluate', 'judgement', 'effective', 'convincing', 'impact', 'evidence', 'balanced judgement'],
      examinerTip:
        'Evaluation is judgement with evidence. It is not enough to say "this is effective"; explain how far and why.',
      commonMistakes: [
        'Only describing the technique.',
        'Using vague judgement words without evidence.',
        'Pretending every technique is equally successful.'
      ],
      grade9Move:
        'Offer a balanced judgement: explain what works, what is less successful, and how this affects the writer’s overall purpose.',
      explainer: {
        title: 'Evaluating Texts',
        content: `
Evaluating a text means making a judgement about how well it achieves its purpose.

Key questions:
- Is the writing effective?
- Does it match its audience and purpose?
- Are the techniques used well?
- Do the techniques actually create the effect the writer wants?

Strong evaluation:
- Uses precise vocabulary: "effective", "powerful", "striking", "convincing", "unconvincing".
- Justifies judgement with evidence.
- Considers both strengths and weaknesses where appropriate.

Useful phrases:
- "This is effective because..."
- "However, the writer's use of X is less successful because..."
- "Overall, the writer achieves..."

Evaluation is a high-level analytical skill because you are judging, not just describing.
`
      },
      interactiveLesson: {
        title: 'Evaluating',
        steps: [
          { type: 'question', question: 'Evaluation means:', options: ['Making a judgement', 'Only summarising'], correct: 0 },
          { type: 'question', question: 'Strong evaluations can consider:', options: ['Strengths and weaknesses', 'Only strengths'], correct: 0 },
          { type: 'question', question: 'Good evaluation uses:', options: ['Precise judgement vocabulary', 'Generic words only'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text claim:
The writer uses a list of three to persuade.

Evaluation:
"The list of three 'safer, stronger, freer' is effective because it builds a rhythmic, memorable impression. However, the vagueness of 'freer' weakens the specific impact of the claim. Overall, the technique works well rhythmically but less well logically."
`
      },
      practice: [
        { type: 'mcq', question: 'Which is an evaluative word?', options: ['"Effective"', '"And"'], correct: 0 },
        { type: 'mcq', question: 'Which is stronger evaluation?', options: ['"This is convincing because the statistic is specific."', '"This is good."'], correct: 0 },
        { type: 'true-false', question: 'Evaluation includes judgement, not just description.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Evaluate this line: "Our children's future is hanging by a thread."`,
        sentenceStarter: 'This is effective because...',
        hints: ['Name a technique.', 'Judge its success.', 'Explain why it would affect the reader.']
      },
      aiFeedback: {
        successCriteria: ['Gives a clear judgement', 'Uses evidence', 'Uses precise evaluative vocabulary', 'Explains effect']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why do examiners reward answers that notice where a technique could be weaker?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 28 — PROOFREADING YOUR WRITING
    // =========================================================
    writingSkill({
      id: 'eng-5-28',
      slug: 'proofreading-your-writing',
      title: 'Proofreading Your Writing',
      summary: 'Catch errors before you hand work in.',
      keyVocabulary: ['proofread', 'SPaG', 'homophone', 'tense', 'apostrophe', 'sentence boundary', 'accuracy'],
      examinerTip:
        'Proofreading protects marks. It is especially important where accuracy, clarity and technical control are assessed.',
      commonMistakes: [
        'Reading too quickly and seeing what you meant to write.',
        'Checking spelling but not punctuation.',
        'Ignoring homophones because spell-check does not flag them.'
      ],
      grade9Move:
        'Proofread in layers: sentence boundaries, tense, apostrophes, homophones, spelling, then clarity.',
      quickRecall: [
        'What is the difference between their, there and they’re?',
        'What is the difference between its and it’s?',
        'What is a comma splice?',
        'What tense is the piece mainly written in?',
        'Does every sentence have a clear end mark?'
      ],
      explainer: {
        title: 'Proofreading Your Writing',
        content: `
Proofreading is the final check for spelling, punctuation and grammar errors.

Method:
- Read each sentence slowly.
- Read aloud if possible.
- Check every sentence has a capital letter and an end mark.
- Check apostrophes: its/it's, your/you're.
- Check homophones: their/there/they're, to/too/two, where/were/wear.
- Check for missing words.
- Check tense stays consistent.
- Check commas do not create comma splices.

In exams, leave a few minutes at the end for proofreading if possible.

Careful proofreading can protect valuable marks and make your writing clearer.
`
      },
      interactiveLesson: {
        title: 'Proofreading',
        steps: [
          { type: 'question', question: 'Proofreading checks:', options: ['Spelling, punctuation and grammar', 'Structure only'], correct: 0 },
          { type: 'question', question: 'Reading aloud helps you:', options: ['Hear missing or awkward words', 'Write faster'], correct: 0 },
          { type: 'question', question: 'In exams, it is sensible to reserve:', options: ['A few minutes for checking', 'No time at all'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"Their going to the shop. Its closer then you think, and there dog is waiting."

Corrected:
"They're going to the shop. It's closer than you think, and their dog is waiting."

Three homophone errors are corrected:
- Their → They're
- Its → It's
- there → their
Also "then" becomes "than".
`
      },
      practice: [
        { type: 'mcq', question: '"Their", "there" and "they\'re" are:', options: ['Homophones', 'Synonyms'], correct: 0 },
        { type: 'mcq', question: 'Which means "you are"?', options: ["you're", 'your'], correct: 0 },
        { type: 'true-false', question: 'Proofreading is optional if you want the strongest possible answer.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Find and fix errors: "its been a long week and the students are tireder then ever, their homeworks are late to"`,
        sentenceStarter: "It's been a long week...",
        hints: ['Check "its" vs "it\'s".', 'Check "then" vs "than".', 'Check "to" vs "too".', 'Check awkward comparative wording.']
      },
      aiFeedback: {
        successCriteria: ['Fixes apostrophes', 'Fixes homophones', 'Keeps meaning', 'Improves awkward phrasing']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does reading aloud catch errors that silent reading misses?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 29 — WARM-UP AND WORKED PRACTICE
    // =========================================================
    writingSkill({
      id: 'eng-5-29',
      slug: 'warm-up-and-worked-practice-questions-improving-writing',
      title: 'Warm-Up and Worked Practice Questions',
      summary: 'Scaffolded practice for writing skills.',
      keyVocabulary: ['warm-up', 'worked example', 'model answer', 'topic sentence', 'show don’t tell', 'SPaG'],
      examinerTip:
        'Warm-up tasks build accuracy before longer writing. Do not rush them: they train the habits that show in extended answers.',
      commonMistakes: [
        'Skipping the worked example.',
        'Copying the model without understanding why it works.',
        'Practising skills in isolation and not applying them to real writing.'
      ],
      grade9Move:
        'Study model answers actively: identify the verb choice, sentence rhythm, punctuation choices and how the example creates effect.',
      quickRecall: [
        'What is a topic sentence?',
        'What does show don’t tell mean?',
        'What is the difference between its and it’s?',
        'What is an embedded quotation?',
        'How can a short sentence create impact?'
      ],
      explainer: {
        title: 'Warm-Up and Worked Practice',
        content: `
Warm-up questions check:
- Basic punctuation and grammar
- Paragraph structure
- Technique naming
- Sentence control
- Vocabulary precision

Worked practice questions show you a model of effective writing. Study:
- The topic sentence
- How quotes are used
- How explanations zoom in
- How sentences vary in length
- How unnecessary words are cut
- How punctuation supports clarity

Treat this topic as rehearsal before longer independent practice.
`
      },
      interactiveLesson: {
        title: 'Warm-Up',
        steps: [
          { type: 'question', question: 'A topic sentence appears:', options: ['At the start of a paragraph', 'Only at the end of an essay'], correct: 0 },
          { type: 'question', question: '"Its" without an apostrophe means:', options: ['Belonging to it', 'It is'], correct: 0 },
          { type: 'question', question: 'Varying sentence length creates:', options: ['Pace and emphasis', 'Typing errors'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Practice Question',
        content: `
Question:
Improve this sentence:
"She was very happy and smiled a lot."

Model answer:
"Her face broke into a wide, unstoppable grin."

Why it works:
The new version shows happiness through body language and uses stronger vocabulary. It also cuts "very", which adds little.
`
      },
      practice: [
        { type: 'mcq', question: '"Showing" over "telling" means:', options: ['Using behaviour to reveal feeling', 'Using more words'], correct: 0 },
        { type: 'mcq', question: 'A model answer should be:', options: ['Studied for method', 'Copied blindly every time'], correct: 0 },
        { type: 'true-false', question: 'Worked examples help you learn good habits.', correct: true }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite this dull sentence with better vocabulary and "show don\'t tell": "He was nervous and walked to the door."`,
        sentenceStarter: '',
        hints: ['Replace "nervous" with action.', 'Use a more precise verb than "walked".']
      },
      aiFeedback: {
        successCriteria: ['Shows not tells', 'Strong verb choice', 'Improved sentence rhythm']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does show don’t tell make writing feel more professional?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 30 — PRACTICE QUESTIONS
    // =========================================================
    writingSkill({
      id: 'eng-5-30',
      slug: 'practice-questions-improving-writing',
      title: 'Practice Questions',
      summary: 'Independent writing-skill practice.',
      keyVocabulary: ['practice', 'planning', 'editing', 'description', 'narrative', 'sentence variety', 'imagery'],
      examinerTip:
        'Independent practice should combine skills: planning, vocabulary, structure, sentence rhythm and proofreading.',
      commonMistakes: [
        'Starting without a plan.',
        'Using techniques randomly rather than for effect.',
        'Forgetting to proofread the final answer.'
      ],
      grade9Move:
        'Before writing, choose the intended mood. Then select vocabulary, imagery and sentence rhythm that all support that mood.',
      explainer: {
        title: 'Practice Questions',
        content: `
Now work independently.

Before you write:
- Read the task carefully.
- Plan briefly: key points, vocabulary, structure and mood.

When you write:
- Use topic sentences where appropriate.
- Vary sentence lengths.
- Use strong verbs and nouns.
- Use short embedded quotes where appropriate.
- Choose precise vocabulary.
- Use punctuation for clarity and effect.

After you write:
- Proofread for SPaG.
- Read aloud if possible.
- Cut weak words such as "very", "really" and "quite".
`
      },
      interactiveLesson: {
        title: 'Exam Habits',
        steps: [
          { type: 'question', question: 'The first step when writing is usually:', options: ['Plan briefly', 'Start immediately with no thought'], correct: 0 },
          { type: 'question', question: 'Cuttable weak words include:', options: ['"Very" and "really"', '"Because" and "therefore"'], correct: 0 },
          { type: 'question', question: 'Reading aloud helps:', options: ['Check rhythm and flow', 'Count syllables only'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Example',
        content: `
Prompt:
Describe a busy street.

Model:
"Traffic crawled. Pavements swelled with umbrellas, shopping bags and impatient shoulders. Somewhere, a siren rose and fell like a warning no one had time to hear."

Why it works:
The paragraph varies sentence lengths, uses strong verbs such as "crawled" and "swelled", and includes a simile for the siren.
`
      },
      practice: [
        { type: 'mcq', question: 'Strong description uses:', options: ['Strong verbs and imagery', 'Long lists of adjectives only'], correct: 0 },
        { type: 'mcq', question: 'A planned mood helps you:', options: ['Choose fitting vocabulary and structure', 'Ignore punctuation'], correct: 0 },
        { type: 'true-false', question: 'You should never plan before writing.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Write 3–4 sentences describing a quiet classroom at dusk.`,
        sentenceStarter: '',
        hints: ['Vary sentence length.', 'Use strong verbs.', 'Include one comparison.', 'Create a clear mood.']
      },
      aiFeedback: {
        successCriteria: ['Varied sentence length', 'Strong verbs', 'At least one literary technique', 'Clear mood']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does describing through senses such as sound and smell often feel more powerful than describing only appearance?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 31 — SPAG KNOWLEDGE ORGANISER
    // =========================================================
    writingSkill({
      id: 'eng-5-31',
      slug: 'spelling-punctuation-and-grammar-knowledge-organiser',
      title: 'Spelling, Punctuation and Grammar — Knowledge Organiser',
      summary: 'Quick-reference summary of SPaG rules.',
      keyVocabulary: ['SPaG', 'punctuation', 'grammar', 'spelling', 'homophone', 'Standard English', 'sentence boundary'],
      examinerTip:
        'SPaG knowledge is most useful when applied automatically. Use this organiser for quick retrieval, then apply it in real writing.',
      commonMistakes: [
        'Knowing the rule but forgetting to use it under pressure.',
        'Checking punctuation but ignoring tense.',
        'Confusing homophones that spell-check will not catch.'
      ],
      grade9Move:
        'Turn SPaG into a checklist: sentence boundaries, tense, agreement, apostrophes, homophones, punctuation variety and spelling.',
      quickRecall: [
        'What is a comma splice?',
        'What is the difference between its and it’s?',
        'What is the difference between your and you’re?',
        'What is subject–verb agreement?',
        'What is Standard English?',
        'What does a semicolon join?',
        'When do you use a colon?'
      ],
      explainer: {
        title: 'SPaG Knowledge Organiser',
        content: `
Use this summary to revise SPaG rules.

Punctuation:
- Full stops, question marks and exclamation marks close sentences.
- Commas separate list items, clauses, extra information and introductory phrases.
- Avoid comma splices.
- Colons introduce lists, explanations or quotations.
- Semicolons join related main clauses or separate complex list items.
- Apostrophes show contractions and possession.
- "its" means belonging to it; "it's" means it is or it has.
- Inverted commas mark direct speech and quotations.
- Brackets add non-essential information.
- Dashes add emphasis or interruption.
- Hyphens join words.

Grammar:
- Simple, compound and complex sentences.
- Subject–verb agreement.
- Standard English in formal writing.
- No double negatives in formal Standard English.
- Keep tense consistent.

Spelling:
- Watch homophones: their/there/they're, your/you're, to/too/two, where/were/wear.
- Use spell-check if available, but do not trust it blindly.
`
      },
      interactiveLesson: {
        title: 'SPaG Quick Recall',
        steps: [
          { type: 'question', question: '"It\'s" means:', options: ['It is or it has', 'Belonging to it'], correct: 0 },
          { type: 'question', question: '"They\'re" means:', options: ['They are', 'Their place'], correct: 0 },
          { type: 'question', question: 'A semicolon can:', options: ['Join two related main clauses', 'End a question'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Summary',
        content: `
Text:
"its them over their their going to there house"

Corrected:
"It's them over there; they're going to their house."

Rules applied:
- It's = it is.
- There = place.
- They're = they are.
- Their = belonging to them.
- Semicolon joins two related main clauses.
`
      },
      practice: [
        { type: 'mcq', question: '"Your" means:', options: ['Belonging to you', 'You are'], correct: 0 },
        { type: 'mcq', question: 'A comma splice is:', options: ['Two sentences joined only by a comma', 'A list of three nouns'], correct: 0 },
        { type: 'true-false', question: 'Good SPaG is only needed in essays, not creative writing.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Rewrite with correct SPaG: "my sisters friends was at there house and there happy because their exam was easier then expected"`,
        sentenceStarter: "My sister's friends...",
        hints: ['Check apostrophes.', 'Check subject–verb agreement.', 'Check homophones.', 'Check than/then.']
      },
      aiFeedback: {
        successCriteria: ['Apostrophes correct', 'Verb agreement correct', 'Homophones correct', 'Sentence punctuation correct']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does SPaG matter even for creative writing, where rules can sometimes be broken deliberately?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 32 — IMPROVING WRITING KNOWLEDGE ORGANISER
    // =========================================================
    writingSkill({
      id: 'eng-5-32',
      slug: 'improving-your-writing-knowledge-organiser',
      title: 'Improving Your Writing — Knowledge Organiser',
      summary: 'Quick-reference summary for writing craft.',
      keyVocabulary: ['voice', 'variety', 'structure', 'analysis', 'evaluation', 'comparison', 'proofreading', 'craft'],
      examinerTip:
        'The strongest writers combine clarity, control and style. Technique only matters when it helps meaning.',
      commonMistakes: [
        'Thinking Grade 9 writing means complicated writing.',
        'Using techniques without purpose.',
        'Forgetting that clarity comes before style.'
      ],
      grade9Move:
        'Master the hierarchy: clarity first, control second, style third. The best writing feels effortless because every choice has a purpose.',
      quickRecall: [
        'What does TIPT stand for?',
        'What does show don’t tell mean?',
        'What is an embedded quotation?',
        'How do you explain a quotation?',
        'What is evaluation?',
        'Why is point-by-point comparison useful?',
        'Which weak words can often be cut?'
      ],
      explainer: {
        title: 'Improving Your Writing Knowledge Organiser',
        content: `
Use this summary to revise writing craft.

Key ideas:
- Variety: mix sentence types and lengths.
- Voice: let personality show through precise word choice.
- Show don't tell: reveal feelings through body language, actions and behaviour.
- Vocabulary: prefer strong verbs and nouns; cut weak intensifiers such as "very" and "really".
- Structure: use paragraphs, TIPT, topic sentences and logical links.
- Quotes: short, embedded and accurate.
- Analysis: name a method, zoom in on a word, explore effect, link to purpose.
- Comparison: use point-by-point structure and signposts such as both, similarly, whereas and however.
- Evaluation: make a judgement and justify it with evidence.
- Essays: plan, write, edit, proofread.
- SPaG: control sentence boundaries, punctuation, homophones and tense.

Aim for:
1. Clarity
2. Control
3. Sharpness
4. Style
`
      },
      interactiveLesson: {
        title: 'Writing Recall Quiz',
        steps: [
          { type: 'question', question: '"Show don\'t tell" means:', options: ['Show emotion through action', 'Use pictures'], correct: 0 },
          { type: 'question', question: 'Point-by-point is best for:', options: ['Comparison essays', 'Spelling tests'], correct: 0 },
          { type: 'question', question: 'Strong analysis links evidence to:', options: ['The writer\'s purpose', 'Your favourite film'], correct: 0 },
          { type: 'question', question: 'Cuttable weak words include:', options: ['"Very" and "really"', '"Because"'], correct: 0 }
        ]
      },
      workedExample: {
        title: 'Worked Summary',
        content: `
Weak:
"The writer really uses very strong words to make it really effective."

Strong:
"The writer's precise vocabulary, such as 'hollowed' and 'ashen', creates a striking sense of decay."

Why it works:
- Weak intensifiers are cut.
- Specific quotation is added.
- The effect is judged clearly.
`
      },
      practice: [
        { type: 'mcq', question: 'TIPT is a memory aid for:', options: ['When to start a new paragraph', 'Spelling rules'], correct: 0 },
        { type: 'mcq', question: '"Effective" is a strong word for:', options: ['Evaluation', 'Addition'], correct: 0 },
        { type: 'true-false', question: 'Editing usually weakens writing.', correct: false }
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: `Pick three ideas from Section 5 and explain how each will improve your writing this week.`,
        sentenceStarter: 'Firstly,...',
        hints: ['Pick different areas, such as vocabulary, structure and punctuation.', 'Explain how each skill changes your writing.']
      },
      aiFeedback: {
        successCriteria: ['Chooses three distinct ideas', 'Explains each', 'Shows a clear writing improvement plan']
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: `Which one writing skill, if mastered first, gives the biggest leap in grade? Justify your answer.`,
        xpReward: 50
      }
    })
  ]
};

export const getEnglishContent5TopicCount = () => englishContent5.topics.length;

export default englishContent5;
